import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import NotificationBell from './components/NotificationBell';
import LoginPage from './pages/LoginPage';
import StockPage from './pages/StockPage';
import InvoicePage from './pages/InvoicePage';
import InvoiceListPage from './pages/InvoiceListPage';
import UserManagementPage from './pages/UserManagementPage';
import DashboardPage from './pages/DashboardPage';
import AuditLogsPage from './pages/AuditLogsPage';
import './index.css';

function NavigationBar() {
    const { user, logout, isAuthenticated } = useAuth();
    const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);

    if (!isAuthenticated) return null;

    // Get initials from email
    const getInitials = (email) => {
        if (!email) return 'U';
        return email.substring(0, 2).toUpperCase();
    };

    return (
        <nav className="navbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', width: '100%' }}>
                <img
                    src="https://d2u56hfpsewfc3.cloudfront.net/images/e-con-twenty-plus-years-logo-register.svg"
                    alt="e-con Systems Logo"
                    style={{ height: '35px' }}
                />
                <ul style={{ flex: 1, fontSize: '0.9rem' }}>
                    <li>
                        <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
                            📦 Products
                        </NavLink>
                    </li>

                    {/* Invoice Dropdown */}
                    <li
                        className="nav-dropdown"
                        onMouseEnter={() => setShowInvoiceDropdown(true)}
                        onMouseLeave={() => setShowInvoiceDropdown(false)}
                    >
                        <span className="nav-dropdown-trigger">
                            📋 Sales Order ▾
                        </span>
                        {showInvoiceDropdown && (
                            <div className="nav-dropdown-menu">
                                <NavLink to="/invoices" className="nav-dropdown-item">
                                    📋 SO List
                                </NavLink>
                                <NavLink to="/invoice" className="nav-dropdown-item">
                                    ➕ Create SO
                                </NavLink>
                            </div>
                        )}
                    </li>

                    {user?.role === 'admin' && (
                        <>
                            <li>
                                <NavLink to="/users" className={({ isActive }) => isActive ? 'active' : ''}>
                                    👥 Users
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'active' : ''}>
                                    📊 Dashboard
                                </NavLink>
                            </li>
                            <li>
                                <NavLink to="/audit-logs" className={({ isActive }) => isActive ? 'active' : ''}>
                                    📋 Logs
                                </NavLink>
                            </li>
                        </>
                    )}
                </ul>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {/* Notification Bell */}
                    <NotificationBell />

                    {/* User Avatar with Tooltip */}
                    <div style={{ position: 'relative', display: 'inline-block' }} className="user-avatar-container">
                        <div style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '700',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            border: '2px solid rgba(255,255,255,0.3)'
                        }}>
                            {getInitials(user?.email)}
                        </div>
                        <div className="user-tooltip">
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{user?.name}</div>
                            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>{user?.email}</div>
                            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', padding: '0.25rem 0.5rem', background: 'rgba(255,255,255,0.2)', borderRadius: '4px', display: 'inline-block' }}>
                                {user?.role}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        className="btn-secondary"
                        style={{
                            padding: '0.45rem 0.9rem',
                            fontSize: '0.85rem',
                            background: 'rgba(255,255,255,0.2)',
                            border: '1px solid white',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            fontWeight: '600'
                        }}
                    >
                        🚪 Logout
                    </button>
                </div>
            </div>
        </nav>
    );
}

function AppRoutes() {
    return (
        <div className="App">
            <div className="container">
                <NavigationBar />

                <Routes>
                    <Route path="/login" element={<LoginPage />} />

                    <Route path="/" element={
                        <PrivateRoute>
                            <StockPage />
                        </PrivateRoute>
                    } />

                    <Route path="/invoices" element={
                        <PrivateRoute>
                            <InvoiceListPage />
                        </PrivateRoute>
                    } />

                    <Route path="/invoice" element={
                        <PrivateRoute>
                            <InvoicePage />
                        </PrivateRoute>
                    } />

                    <Route path="/invoice/edit/:id" element={
                        <PrivateRoute>
                            <InvoicePage />
                        </PrivateRoute>
                    } />

                    <Route path="/users" element={
                        <PrivateRoute requiredRole="admin">
                            <UserManagementPage />
                        </PrivateRoute>
                    } />

                    <Route path="/dashboard" element={
                        <PrivateRoute requiredRole="admin">
                            <DashboardPage />
                        </PrivateRoute>
                    } />

                    <Route path="/audit-logs" element={
                        <PrivateRoute requiredRole="admin">
                            <AuditLogsPage />
                        </PrivateRoute>
                    } />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </div>
        </div>
    );
}

function App() {
    return (
        <Router>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </Router>
    );
}

export default App;
